import React from 'react';
import { connect } from 'react-redux';
import { Alert, Button, DataTable, InfoLabel } from '../../../../common/components';
import Select from 'react-select';
import { TableHeaderColumn } from 'react-bootstrap-table';
import { Col, Row } from 'react-bootstrap';
import displayAddress from '../../../../helpers/displayAddress';
import { push } from 'react-router-redux';
import MergeCustomersButton from '../MergeCustomersButton/MergeCustomersButton';
import { createAccount } from '../../../policy/redux/policyActions';

import { sortBy } from 'lodash';
import { InternalButtonBar } from '../../../layout/components';
import formatDate from '../../../../helpers/formatDate';
import { hasPermission } from '../../../auth/redux/authActions';
import FilterContainer from '../../../policy/components/FilterContainer/FilterContainer';
import { applyFilter } from '../../../../common/components/DataTable/redux/dataTableActions';
import { ICON_MAP } from '../../../../common/components/SvgIcons/productIcons';
import selectTheme from '../../../../helpers/selectTheme';
import AnonymizeCustomerButton from '../AnonymizeCustomerButton/AnonymizeCustomerButton';
import ArchiveCustomerButton from '../ArchiveCustomerButton/ArchiveCustomerButton';
import getIncludedResource from '../../../../helpers/getIncludedResource';
import './CustomerDetails.scss';

const NOT_APPLICABLE = 'not applicable';

const GetQuoteButtons = ({ products, customerId }) => {
  if (!hasPermission('policy.quote')) {
    return null;
  }

  return (
    <Row>
      {products.map((product) => {
        return (
          <Col sm={4}>
            <Button
              //handleClick={onNewQuote}
              className="btn-block"
              bsStyle="primary"
              link
              to={`/products/${product.id}/quote/?customer=${customerId}`}
            >
              <div className={'get-a-quote-btn'}>
                {ICON_MAP[product.attributes.product_code]}
                <span>Get a {product.attributes.name} Quote</span>
              </div>
            </Button>
          </Col>
        );
      })}
    </Row>
  );
};

const schemeType = (cell) => {
  if (!cell.scheme_type) {
    return '';
  }
  return cell.scheme_type.charAt(0).toUpperCase() + cell.scheme_type.slice(1);
};

const destinations = (cell) => {
  if (!cell.destinations) {
    return '';
  }
  let scheme_type = cell.scheme_type;
  let destinations = cell.destinations;

  if (scheme_type === 'single') {
    return destinations
      .toString()
      .split(',')
      .join('<br>');
  } else return '';
};

const CreateAccountButton = ({ customerStatus, customerId, handleCreateUser }) => {
  if (customerStatus !== NOT_APPLICABLE) {
    return null;
  }
  return (
    <Button
      label={'Create An Account'}
      bsStyle="primary"
      className="pull-right"
      handleClick={() => handleCreateUser(customerId)}
    />
  );
};

const CustomerDetails = ({
  onCreateAccount,
  goTo,
  customer,
  onApplyFilter,
  availableProducts,
  onRefresh,
}) => {
  const customerId = customer?.data?.id;
  const customerAttributes = customer.data.attributes;
  const customerStatus = customerAttributes?.status;

  const policies = getIncludedResource(customer.data, customer.included, 'policies');
  const policyApplications = getIncludedResource(
    customer.data,
    customer.included,
    'policy_applications',
  );

  const handleCreateUser = (customerId) => {
    onCreateAccount(customerId);
  };

  const handleRowClick = (row) => {
    return goTo(`/policies/issued/${row.id}`);
  };
  const products = sortBy(availableProducts, 'attributes.name');

  const marketingOptions = Object.keys(customerAttributes?.marketing_options ?? [])
    .filter((option) => customerAttributes.marketing_options[option] > 0)
    .join(',');

  const handleProductChange = (option) => {
    onApplyFilter('policies', 'filter[product]', option?.value ?? '');
  };

  const onCoverPolicies = Array.isArray(policies)
    ? policies.filter((policy) => {
        return ['on cover', 'issued'].includes(policy.attributes.status);
      })
    : [];
  const allPolicies = Array.isArray(policies) ? policies : [];
  const allPolicyApplications = Array.isArray(policyApplications)
    ? policyApplications
    : [];

  return (
    <div className={'customer-details'}>
      <Row>
        <Col xs={12}>
          <Alert />
        </Col>
      </Row>
      <Row>
        <Col sm={6}>
          {customerAttributes.company_name && (
            <InfoLabel
              label="Company Name"
              value={customerAttributes.company_name}
              labelSize={4}
            />
          )}
          <InfoLabel
            label="Name"
            value={customerAttributes.customer_name}
            labelSize={4}
          />
          <InfoLabel label="Date of Birth" value={customerAttributes.dob} labelSize={4} />
          <InfoLabel
            label="Email"
            value={customerAttributes.email}
            labelSize={4}
            isLowerCase
          />
        </Col>

        <Col sm={6}>
          <InfoLabel
            label="Address"
            value={displayAddress(customerAttributes?.address)}
            labelSize={4}
          />
          <InfoLabel label="Phone" value={customerAttributes.phone1} labelSize={4} />
          <InfoLabel
            label="Mobile Number"
            value={customerAttributes.phone2}
            labelSize={4}
          />
        </Col>
      </Row>

      <Row>
        <Col sm={6}>
          <InfoLabel
            className={'capitalize'}
            label="Marketing Options"
            value={marketingOptions}
            labelSize={4}
          />
        </Col>
      </Row>

      <div className="hr transparent" />
      <GetQuoteButtons products={products} customerId={customerId} />
      <div className="hr " />
      {(hasPermission('policy.view') || hasPermission('policy.view_owner')) && (
        <>
          <h3>Policies</h3>
          <Row>
            <Col sm={4}>
              <FilterContainer label={'Product'}>
                <Select
                  theme={selectTheme}
                  onChange={handleProductChange}
                  isClearable
                  options={products.map((product) => ({
                    label: product?.attributes?.name,
                    value: product?.id,
                  }))}
                />
              </FilterContainer>
            </Col>
          </Row>

          <DataTable
            source="local/policies"
            name="policies"
            autoFilterType="filter[customer]"
            autoFilter={customerId}
            showTotal
            onRowSelect={handleRowClick}
            key={customerAttributes.customer_name + allPolicies.length}
          >
            <TableHeaderColumn dataField="id" isKey hidden />

            <TableHeaderColumn dataField="policy_number" dataSort>
              Policy Number
            </TableHeaderColumn>

            <TableHeaderColumn dataField="insured" dataSort>
              Insured
            </TableHeaderColumn>

            <TableHeaderColumn
              dataField="metadata"
              width={'130px'}
              dataSort
              dataFormat={schemeType}
            >
              Type
            </TableHeaderColumn>

            <TableHeaderColumn
              dataField="commences_at"
              width={'130px'}
              dataFormat={formatDate}
            >
              Start Date
            </TableHeaderColumn>

            <TableHeaderColumn
              dataField="expires_at"
              width={'130px'}
              dataFormat={formatDate}
            >
              End Date
            </TableHeaderColumn>

            <TableHeaderColumn
              dataField="metadata"
              width={'150px'}
              dataFormat={destinations}
            >
              Destinations
            </TableHeaderColumn>

            <TableHeaderColumn dataField="status" width={'130px'} dataSort>
              Status
            </TableHeaderColumn>

            <TableHeaderColumn dataField="created_at" dataFormat={formatDate} dataSort>
              Quote Date
            </TableHeaderColumn>

            <TableHeaderColumn dataField="issued_at" dataFormat={formatDate} dataSort>
              Issue Date
            </TableHeaderColumn>
          </DataTable>
        </>
      )}

      <Row>
        <Col xs={12}>
          <InternalButtonBar
            buttons={[
              {
                permissions: ['customer.edit'],
                button: (
                  <Button
                    link
                    to={`/customers/${customerId}/edit`}
                    className="pull-right"
                    bsStyle="secondary"
                    rightIcon="pencil-square-o"
                  >
                    Edit Customer
                  </Button>
                ),
              },
              {
                permissions: ['customer.edit'],
                button: (
                  <MergeCustomersButton
                    customer={customerAttributes}
                    customerId={customerId}
                    onRefresh={onRefresh}
                    className="pull-right pad-right"
                  />
                ),
              },
              {
                permissions: ['customer.edit'],
                button: (
                  <CreateAccountButton
                    customerStatus={customerStatus}
                    customerId={customerId}
                    handleCreateUser={handleCreateUser}
                  />
                ),
              },
              {
                permissions: ['customer.anonymize'],
                button: (
                  <AnonymizeCustomerButton
                    customerId={customerId}
                    customerName={customerAttributes.customer_name}
                    onCoverPolicies={onCoverPolicies}
                    allPolicies={allPolicies}
                    allPolicyApplications={allPolicyApplications}
                  />
                ),
              },
              {
                permissions: ['customer.edit'],
                button: (
                  <ArchiveCustomerButton
                    id={customerId}
                    customerName={customerAttributes.customer_name}
                  />
                ),
              },
            ]}
          />
        </Col>
      </Row>
    </div>
  );
};

export default connect(
  null,
  {
    goTo: push,
    onCreateAccount: createAccount,
    onApplyFilter: applyFilter(),
  },
)(CustomerDetails);
